/**
 * Audio Processing Service
 *
 * Handles client-side audio pre-processing including silence removal.
 */

import { settings, getPauseThresholdDb } from './settingsService';

// Default configuration (can be overridden by settings)
const getConfig = () => {
  const pauseLevel = settings.pauseDetection.get();

  // Convert dB to amplitude (rough approximation)
  const pauseThresholdDb = getPauseThresholdDb(pauseLevel);
  const silenceThreshold = Math.pow(10, pauseThresholdDb / 20);

  return {
    SILENCE_THRESHOLD: silenceThreshold,
    MIN_SILENCE_DURATION_MS: 500,
    KEEP_SILENCE_MS: 100
  };
}

/**
 * Analyze audio blob and return statistics
 * @param {Blob} blob - Audio blob to analyze
 * @returns {Promise<object>} Audio statistics
 */
export const analyzeAudio = async (blob) => {
  const config = getConfig();
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();

  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const duration = audioBuffer.duration;
    const sampleRate = audioBuffer.sampleRate;
    const numberOfChannels = audioBuffer.numberOfChannels;

    // Analyze for silence
    const channelData = audioBuffer.getChannelData(0); // Use first channel
    const silenceRegions = findSilenceRegions(channelData, sampleRate, config);

    const totalSilenceDuration = silenceRegions.reduce(
      (sum, region) => sum + (region.end - region.start),
      0
    );

    return {
      originalDuration: duration,
      originalSize: blob.size,
      sampleRate,
      numberOfChannels,
      silenceRegions,
      totalSilenceDuration,
      speechDuration: duration - totalSilenceDuration,
      silencePercentage: (totalSilenceDuration / duration) * 100
    };
  } finally {
    await audioContext.close();
  }
};

/**
 * Find silence regions in audio data
 * @param {Float32Array} channelData - Audio samples
 * @param {number} sampleRate - Sample rate in Hz
 * @param {object} config - Configuration object with SILENCE_THRESHOLD, MIN_SILENCE_DURATION_MS, KEEP_SILENCE_MS
 * @returns {Array<{start: number, end: number}>} Array of silence regions (in seconds)
 */
const findSilenceRegions = (channelData, sampleRate, config = getConfig()) => {
  const regions = [];
  const minSilenceSamples = (config.MIN_SILENCE_DURATION_MS / 1000) * sampleRate;
  const windowSize = Math.floor(sampleRate * 0.02); // 20ms windows

  let silenceStart = null;

  for (let i = 0; i < channelData.length; i += windowSize) {
    // Calculate RMS amplitude for this window
    let sum = 0;
    const end = Math.min(i + windowSize, channelData.length);
    for (let j = i; j < end; j++) {
      sum += channelData[j] * channelData[j];
    }
    const rms = Math.sqrt(sum / (end - i));

    const isSilent = rms < config.SILENCE_THRESHOLD;
    const currentTime = i / sampleRate;

    if (isSilent && silenceStart === null) {
      silenceStart = currentTime;
    } else if (!isSilent && silenceStart !== null) {
      const silenceDuration = currentTime - silenceStart;
      if (silenceDuration * sampleRate >= minSilenceSamples) {
        // Keep some silence at boundaries
        const keepSeconds = config.KEEP_SILENCE_MS / 1000;
        regions.push({
          start: silenceStart + keepSeconds,
          end: currentTime - keepSeconds
        });
      }
      silenceStart = null;
    }
  }

  // Handle trailing silence
  if (silenceStart !== null) {
    const endTime = channelData.length / sampleRate;
    const silenceDuration = endTime - silenceStart;
    if (silenceDuration * sampleRate >= minSilenceSamples) {
      const keepSeconds = config.KEEP_SILENCE_MS / 1000;
      regions.push({
        start: silenceStart + keepSeconds,
        end: endTime
      });
    }
  }

  return regions.filter(r => r.end > r.start);
};

/**
 * Remove silence from audio blob
 * @param {Blob} blob - Original audio blob
 * @param {object} analysis - Analysis result from analyzeAudio
 * @returns {Promise<{blob: Blob, stats: object}>} Processed blob and stats
 */
export const removeSilence = async (blob, analysis) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();

  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const { silenceRegions } = analysis;

    if (silenceRegions.length === 0) {
      return {
        blob,
        stats: {
          originalDuration: analysis.originalDuration,
          processedDuration: analysis.originalDuration,
          originalSize: analysis.originalSize,
          processedSize: blob.size,
          silenceRemoved: 0,
          compressionRatio: 1
        }
      };
    }

    // Calculate speech regions (inverse of silence regions)
    const speechRegions = getSpeechRegions(
      silenceRegions,
      audioBuffer.duration
    );

    // Calculate new buffer length
    const newDuration = speechRegions.reduce(
      (sum, r) => sum + (r.end - r.start),
      0
    );
    const newLength = Math.floor(newDuration * audioBuffer.sampleRate);

    // Create new buffer with silence removed
    const newBuffer = audioContext.createBuffer(
      audioBuffer.numberOfChannels,
      newLength,
      audioBuffer.sampleRate
    );

    // Copy speech regions to new buffer
    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const oldData = audioBuffer.getChannelData(channel);
      const newData = newBuffer.getChannelData(channel);

      let writePos = 0;
      for (const region of speechRegions) {
        const startSample = Math.floor(region.start * audioBuffer.sampleRate);
        const endSample = Math.floor(region.end * audioBuffer.sampleRate);

        for (let i = startSample; i < endSample && writePos < newLength; i++) {
          newData[writePos++] = oldData[i];
        }
      }
    }

    // Encode back to blob (always uses webm/opus)
    const processedBlob = await encodeAudioBuffer(newBuffer);

    return {
      blob: processedBlob,
      stats: {
        originalDuration: analysis.originalDuration,
        processedDuration: newDuration,
        originalSize: analysis.originalSize,
        processedSize: processedBlob.size,
        silenceRemoved: analysis.totalSilenceDuration,
        compressionRatio: analysis.originalDuration / newDuration,
        speechRegions: speechRegions.length
      }
    };
  } finally {
    await audioContext.close();
  }
};

/**
 * Trim audio from the end of a blob.
 * Used to remove the stop word from recordings when voice stop is triggered.
 *
 * @param {Blob} blob - Original audio blob
 * @param {number} keepDurationMs - Duration to keep from the start (in milliseconds)
 * @returns {Promise<Blob>} Trimmed audio blob
 */
export const trimAudioEnd = async (blob, keepDurationMs) => {
  // Edge case: keepDurationMs is invalid or zero
  if (!keepDurationMs || keepDurationMs <= 0) {
    console.warn('trimAudioEnd: Invalid keepDurationMs, returning original blob');
    return blob;
  }

  const audioContext = new (window.AudioContext || window.webkitAudioContext)();

  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const originalDurationMs = audioBuffer.duration * 1000;

    // Edge case: keepDurationMs >= original duration, return original
    if (keepDurationMs >= originalDurationMs) {
      console.log('trimAudioEnd: keepDurationMs >= original duration, returning original');
      return blob;
    }

    // Calculate samples to keep
    const keepDurationSec = keepDurationMs / 1000;
    const samplesToKeep = Math.floor(keepDurationSec * audioBuffer.sampleRate);

    // Edge case: too few samples
    if (samplesToKeep < audioBuffer.sampleRate * 0.1) {
      console.warn('trimAudioEnd: Would trim to < 100ms, returning original');
      return blob;
    }

    // Create new buffer with trimmed duration
    const newBuffer = audioContext.createBuffer(
      audioBuffer.numberOfChannels,
      samplesToKeep,
      audioBuffer.sampleRate
    );

    // Copy samples from original to new buffer
    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const oldData = audioBuffer.getChannelData(channel);
      const newData = newBuffer.getChannelData(channel);

      for (let i = 0; i < samplesToKeep; i++) {
        newData[i] = oldData[i];
      }
    }

    // Re-encode to blob using existing function
    const trimmedBlob = await encodeAudioBuffer(newBuffer);

    console.log(`trimAudioEnd: Trimmed ${originalDurationMs.toFixed(0)}ms → ${keepDurationMs.toFixed(0)}ms`);

    return trimmedBlob;
  } catch (error) {
    console.error('trimAudioEnd failed:', error);
    // Fallback: return original blob on error
    return blob;
  } finally {
    await audioContext.close();
  }
};

/**
 * Trim audio from the start of a blob.
 * Used to remove the start trigger phrase from recordings when voice start is triggered.
 *
 * @param {Blob} blob - Original audio blob
 * @param {number} skipMs - Duration to skip from the start (in milliseconds)
 * @returns {Promise<Blob>} Trimmed audio blob
 */
export const trimAudioStart = async (blob, skipMs) => {
  // Edge case: skipMs is invalid or zero
  if (!skipMs || skipMs <= 0) {
    console.warn('trimAudioStart: Invalid skipMs, returning original blob');
    return blob;
  }

  const audioContext = new (window.AudioContext || window.webkitAudioContext)();

  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const originalDurationMs = audioBuffer.duration * 1000;

    // Edge case: skipMs >= original duration, return original (nothing left)
    if (skipMs >= originalDurationMs - 100) {
      console.warn('trimAudioStart: skipMs would remove entire audio, returning original');
      return blob;
    }

    // Calculate samples to skip and keep
    const skipSamples = Math.floor((skipMs / 1000) * audioBuffer.sampleRate);
    const samplesToKeep = audioBuffer.length - skipSamples;

    // Edge case: too few samples remaining
    if (samplesToKeep < audioBuffer.sampleRate * 0.1) {
      console.warn('trimAudioStart: Would keep < 100ms, returning original');
      return blob;
    }

    // Create new buffer with trimmed duration
    const newBuffer = audioContext.createBuffer(
      audioBuffer.numberOfChannels,
      samplesToKeep,
      audioBuffer.sampleRate
    );

    // Copy samples from original to new buffer (skip first skipSamples)
    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const oldData = audioBuffer.getChannelData(channel);
      const newData = newBuffer.getChannelData(channel);

      // Copy from skipSamples to end
      for (let i = 0; i < samplesToKeep; i++) {
        newData[i] = oldData[skipSamples + i];
      }
    }

    // Re-encode to blob using existing function
    const trimmedBlob = await encodeAudioBuffer(newBuffer);

    console.log(`trimAudioStart: Skipped ${skipMs.toFixed(0)}ms, kept ${(originalDurationMs - skipMs).toFixed(0)}ms`);

    return trimmedBlob;
  } catch (error) {
    console.error('trimAudioStart failed:', error);
    // Fallback: return original blob on error
    return blob;
  } finally {
    await audioContext.close();
  }
};

/**
 * Get speech regions (inverse of silence regions)
 */
const getSpeechRegions = (silenceRegions, totalDuration) => {
  if (silenceRegions.length === 0) {
    return [{ start: 0, end: totalDuration }];
  }

  const speechRegions = [];
  let currentStart = 0;

  for (const silence of silenceRegions) {
    if (silence.start > currentStart) {
      speechRegions.push({ start: currentStart, end: silence.start });
    }
    currentStart = silence.end;
  }

  if (currentStart < totalDuration) {
    speechRegions.push({ start: currentStart, end: totalDuration });
  }

  return speechRegions;
};

/**
 * Get the best supported webm mime type with opus codec
 */
const getWebmMimeType = () => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm;codecs=vorbis',
    'audio/webm'
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return 'audio/webm';
};

/**
 * Encode AudioBuffer back to a Blob
 * Uses MediaRecorder to encode with proper webm/opus format
 */
const encodeAudioBuffer = async (audioBuffer) => {
  // Use default sample rate (browser's preferred) to avoid compatibility issues
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();

  // Resume context if suspended (required on some browsers)
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }

  // If sample rates don't match, resample the buffer
  let bufferToPlay = audioBuffer;
  if (audioBuffer.sampleRate !== audioContext.sampleRate) {
    const offlineCtx = new OfflineAudioContext(
      audioBuffer.numberOfChannels,
      Math.ceil(audioBuffer.duration * audioContext.sampleRate),
      audioContext.sampleRate
    );
    const source = offlineCtx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offlineCtx.destination);
    source.start(0);
    bufferToPlay = await offlineCtx.startRendering();
  }

  // Create a MediaStream from the buffer
  const dest = audioContext.createMediaStreamDestination();

  // Add a gain node to ensure signal passes through
  const gainNode = audioContext.createGain();
  gainNode.gain.value = 1.0;

  const bufferSource = audioContext.createBufferSource();
  bufferSource.buffer = bufferToPlay;
  bufferSource.connect(gainNode);
  gainNode.connect(dest);

  // Get the best supported mime type (prefer opus)
  const mimeType = getWebmMimeType();

  // Record the stream
  const blob = await new Promise((resolve, reject) => {
    const chunks = [];
    let stopped = false;

    const recorder = new MediaRecorder(dest.stream, {
      mimeType,
      audioBitsPerSecond: 128000
    });

    const stopRecording = () => {
      if (stopped) return;
      stopped = true;
      if (recorder.state === 'recording') {
        recorder.stop();
      }
    };

    // Fallback timeout - add extra buffer time
    const timeoutMs = Math.max((bufferToPlay.duration * 1000) + 2000, 5000);
    const fallbackTimeout = setTimeout(stopRecording, timeoutMs);

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };

    recorder.onstop = () => {
      clearTimeout(fallbackTimeout);
      const resultBlob = new Blob(chunks, { type: mimeType });
      if (resultBlob.size === 0) {
        reject(new Error('Encoded blob is empty - MediaRecorder captured no data'));
      } else {
        resolve(resultBlob);
      }
    };

    recorder.onerror = (e) => {
      clearTimeout(fallbackTimeout);
      reject(e.error || new Error('MediaRecorder failed'));
    };

    // Use 'ended' event for when playback completes
    bufferSource.onended = () => {
      // Delay to ensure all data is captured
      setTimeout(stopRecording, 500);
    };

    // Start recording first, wait for it to be ready
    recorder.start(100);

    // Wait a bit, then start playback
    setTimeout(() => bufferSource.start(0), 100);
  });

  // Close context after we have the blob
  await audioContext.close();

  return blob;
};

/**
 * Format duration in mm:ss
 */
export const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format file size
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const CONFIG = getConfig();
